import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { enhancedApi } from '../services/api';
import { Pagination } from '../components/Pagination';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Users, 
  TrendingUp,
  Calendar,
  User,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Plus
} from 'lucide-react';

interface DashboardStats {
  totalAudits: number;
  pendingAudits: number;
  completedAudits: number;
  overdueAudits: number;
  teamMembers?: number;
  monthlyTrend?: number;
  mortalityCases?: number;
  morbidityCases?: number;
  mortalityAudits?: number;
  morbidityAudits?: number;
}

interface RecentAudit {
  id: number;
  case_type?: string;
  case_id?: number;
  patient_id: string;
  patient_name?: string;
  status: string;
  assigned_date: string;
  due_date: string;
  auditor_name: string;
}


export const Dashboard: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentAudits, setRecentAudits] = useState<RecentAudit[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchRecentAudits();
  }, [currentPage, itemsPerPage]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats based on user role
      const statsResponse = await enhancedApi.dashboard.getStats();
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
      
      // Fetch recent audits with pagination
      await fetchRecentAudits();

      // EMR sync status removed
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentAudits = async () => {
    try {
      const auditsResponse = await enhancedApi.dashboard.getRecentAudits({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (auditsResponse.success) {
        setRecentAudits(auditsResponse.data);
        
        // Update pagination info
        const responseWithPagination = auditsResponse as any;
        if (responseWithPagination.pagination) {
          setTotalPages(responseWithPagination.pagination.pages);
          setTotalItems(responseWithPagination.pagination.total);
        }
      }
    } catch (error) {
      console.error('Failed to fetch recent audits:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      pending: 'medical-badge-warning',
      assigned: 'medical-badge-info',
      in_progress: 'medical-badge-info',
      submitted: 'medical-badge-warning',
      approved: 'medical-badge-success',
      overdue: 'medical-badge-danger'
    };
    
    return statusClasses[status as keyof typeof statusClasses] || 'medical-badge bg-neutral-100 text-neutral-800 border border-neutral-200';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Welcome Section */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
         
            <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
              <h1 className="text-2xl font-bold text-white">
                  Welcome back, {user?.first_name || 'User'}! 👋
                </h1>
                <p className="text-blue-100 flex items-center space-x-3">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">
                    {user?.department_name}
                  </span>
                  <span className="text-white opacity-60">•</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">
                    {user?.unit_name}
                  </span>
                </p>
              </div>
            </div>

         
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-300 shadow-lg">
                <FileText className="h-8 w-8 text-blue-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.totalAudits}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Total Audits</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-blue-50 rounded-xl px-4 py-3">
              <TrendingUp className="h-4 w-4 mr-2 text-green-600" />
              <span className="font-medium">All time</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl group-hover:from-yellow-200 group-hover:to-yellow-300 transition-all duration-300 shadow-lg">
                <Clock className="h-8 w-8 text-yellow-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.pendingAudits}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Pending</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-yellow-50 rounded-xl px-4 py-3">
              <Clock className="h-4 w-4 mr-2 text-yellow-600" />
              <span className="font-medium">Awaiting review</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl group-hover:from-green-200 group-hover:to-green-300 transition-all duration-300 shadow-lg">
                <CheckCircle className="h-8 w-8 text-green-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.completedAudits}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Completed</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-green-50 rounded-xl px-4 py-3">
              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
              <span className="font-medium">Successfully finished</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl group-hover:from-red-200 group-hover:to-red-300 transition-all duration-300 shadow-lg">
                <AlertCircle className="h-8 w-8 text-red-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.overdueAudits}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Overdue</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-red-50 rounded-xl px-4 py-3">
              <AlertCircle className="h-4 w-4 mr-2 text-red-600" />
              <span className="font-medium">Requires attention</span>
            </div>
          </div>
        </div>
      )}

      {/* Additional Stats Cards for Superuser */}
      {stats && hasRole('superuser') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl group-hover:from-purple-200 group-hover:to-purple-300 transition-all duration-300 shadow-lg">
                <FileText className="h-8 w-8 text-purple-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.mortalityCases || 0}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Mortality Cases</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-purple-50 rounded-xl px-4 py-3">
              <FileText className="h-4 w-4 mr-2 text-purple-600" />
              <span className="font-medium">Total cases</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-2xl group-hover:from-indigo-200 group-hover:to-indigo-300 transition-all duration-300 shadow-lg">
                <FileText className="h-8 w-8 text-indigo-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.morbidityCases || 0}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Morbidity Cases</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-indigo-50 rounded-xl px-4 py-3">
              <FileText className="h-4 w-4 mr-2 text-indigo-600" />
              <span className="font-medium">Total cases</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl group-hover:from-pink-200 group-hover:to-pink-300 transition-all duration-300 shadow-lg">
                <CheckCircle className="h-8 w-8 text-pink-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.mortalityAudits || 0}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Mortality Audits</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-pink-50 rounded-xl px-4 py-3">
              <CheckCircle className="h-4 w-4 mr-2 text-pink-600" />
              <span className="font-medium">Assigned audits</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 group hover:scale-105 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-gradient-to-br from-teal-100 to-teal-200 rounded-2xl group-hover:from-teal-200 group-hover:to-teal-300 transition-all duration-300 shadow-lg">
                <CheckCircle className="h-8 w-8 text-teal-700" />
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-gray-900 mb-1">{stats.morbidityAudits || 0}</p>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Morbidity Audits</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 bg-teal-50 rounded-xl px-4 py-3">
              <CheckCircle className="h-4 w-4 mr-2 text-teal-600" />
              <span className="font-medium">Assigned audits</span>
            </div>
          </div>
        </div>
      )}


      {/* Recent Audits */}
      <div className="medical-card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Audits</h3>
          <div className="p-2 bg-primary-100 rounded-lg">
            <FileText className="h-5 w-5 text-primary-600" />
          </div>
        </div>
        
        {recentAudits.length > 0 ? (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Case Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auditor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentAudits.map((audit) => (
                    <tr key={audit.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{audit.patient_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          audit.case_type === 'mortality' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {audit.case_type === 'mortality' ? 'Mortality' : 'Morbidity'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(audit.status)}>
                          {audit.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(audit.assigned_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(audit.due_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {audit.auditor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/audit/${audit.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No recent audits</h3>
            <p className="text-gray-500">Start by creating your first audit or check back later for updates.</p>
          </div>
        )}
        
        {/* Pagination */}
        {recentAudits.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[5, 10, 20, 50]}
          />
        )}
      </div>
    </div>
  );
};
